import React, { useEffect, useMemo, useState } from 'react';
import { Check, MailWarning } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Checkbox, Field, Input, Radio, Select } from '../ui/Field';
import { pillPrimary, pillSecondary } from '../ui/pills';
import { messageErreur, type LigneDroits, type RoleEquipe, type SalarieRattachable, type ServiceSimple } from '../../lib/equipeApi';
import { CATALOGUE_MODULES } from '../../../shared/catalogueModules.js';
import { useEquipe } from './EquipeShell';
import { phraseDroits, prenomDe } from './droitsPhrase';

/** Invitation (maquette L02-02, modale 640) : identité, rôle de départ, périmètre, modèle, aperçu en phrase. */

const ROLES: { value: RoleEquipe; label: string; desc: string }[] = [
    { value: 'responsable_service', label: 'Responsable de service', desc: 'Gère un service : équipe, planning, réservations.' },
    { value: 'collaborateur', label: 'Collaborateur', desc: 'Droits par module et par action, réglés ensuite.' },
    { value: 'prestataire', label: 'Prestataire externe', desc: 'Périmètre très limité, avec date d\'expiration.' },
];
const LIBELLES = Object.fromEntries((CATALOGUE_MODULES as { name: string; label: string }[]).map((m) => [m.name, m.label]));

interface Props { open: boolean; services: ServiceSimple[]; onClose: () => void; onFait: () => void }

export const InvitationModal: React.FC<Props> = ({ open, services, onClose, onFait }) => {
    const { api } = useEquipe();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [role, setRole] = useState<RoleEquipe>('collaborateur');
    const [svc, setSvc] = useState<string[]>([]);
    const [modele, setModele] = useState('');
    const [expire, setExpire] = useState('');
    const [droits, setDroits] = useState<Partial<LigneDroits>[]>([]);
    const [erreur, setErreur] = useState('');
    const [champErreur, setChampErreur] = useState<{ nom?: string; email?: string; svc?: string }>({});
    const [envoi, setEnvoi] = useState(false);
    const [resultat, setResultat] = useState<{ envoye: boolean; erreur: string | null } | null>(null);
    const [salaries, setSalaries] = useState<SalarieRattachable[]>([]);
    const [employeId, setEmployeId] = useState('');

    useEffect(() => {
        if (!open) return;
        setEmployeId('');
        let vivant = true;
        api.employes().then((r) => { if (vivant) setSalaries(r.employes); }).catch(() => { if (vivant) setSalaries([]); });
        return () => { vivant = false; };
    }, [open, api]);
    // Choisir un salarié reprend son nom et son adresse (modifiables) ; revenir à « Nouvelle personne » vide les deux champs.
    const choisirSalarie = (id: string) => {
        setEmployeId(id);
        const s = salaries.find((x) => x.id === id);
        setName(s ? s.nom : ''); setEmail(s ? s.email : '');
    };

    useEffect(() => {
        if (!open) return;
        setName(''); setEmail(''); setRole('collaborateur'); setSvc(services.length === 1 ? [services[0].id] : []); setModele(''); setExpire(''); setDroits([]); setErreur(''); setChampErreur({}); setResultat(null);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- réinitialise à l'ouverture seulement, pas quand la liste se recharge
    useEffect(() => {
        if (!modele) { setDroits([]); return; }
        let vivant = true;
        api.modele(modele).then((m) => { if (vivant) setDroits(m.droits); }).catch(() => { if (vivant) setDroits([]); });
        return () => { vivant = false; };
    }, [modele, api]);

    const phrase = useMemo(() => (name.trim() ? phraseDroits(prenomDe(name), droits, LIBELLES) : ''), [name, droits]);

    const envoyer = async () => {
        const ce: typeof champErreur = {};
        if (!name.trim()) ce.nom = 'Saisissez le nom complet.';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) ce.email = email.trim() ? `L'adresse « ${email.trim()} » semble incomplète.` : 'Saisissez une adresse e-mail.';
        if (svc.length === 0) ce.svc = 'Choisissez au moins un service.';
        setChampErreur(ce);
        if (Object.keys(ce).length) return;
        setEnvoi(true); setErreur('');
        try {
            const r = await api.inviter({ name: name.trim(), email: email.trim(), role_equipe: role, service_ids: svc, modele: modele || null, employe_id: employeId || null, expire_le: role === 'prestataire' && expire ? expire : null });
            setResultat({ envoye: r.email_envoye, erreur: r.email_erreur });
            onFait();
        } catch (e) { setErreur(messageErreur(e)); }
        finally { setEnvoi(false); }
    };

    return (
        <Modal
            open={open} onClose={onClose} size="lg" title="Inviter une personne"
            subtitle={`Elle recevra un lien pour créer son propre identifiant. Valable 7 jours.`}
            footer={resultat
                ? <button type="button" className={`${pillPrimary(36)} max-md:!h-11`} onClick={onClose}>Terminer</button>
                : <><button type="button" className={`${pillSecondary(36)} max-md:!h-11`} onClick={onClose}>Annuler</button><button type="button" className={`${pillPrimary(36)} max-md:!h-11`} disabled={envoi} onClick={envoyer}>Envoyer l'invitation</button></>}
        >
            {resultat ? (
                <div className="py-4 text-center space-y-2" role="status">
                    <span className={`mx-auto w-11 h-11 rounded-full flex items-center justify-center ${resultat.envoye ? 'bg-state-ok-soft text-state-ok' : 'bg-state-warn-soft text-state-warn'}`}>{resultat.envoye ? <Check size={20} /> : <MailWarning size={20} />}</span>
                    <div className="text-[15px] font-semibold text-content">{resultat.envoye ? 'Invitation envoyée' : "L'invitation est créée, mais l'e-mail n'a pas pu partir"}</div>
                    <p className="text-[13px] text-content-muted max-w-md mx-auto">{resultat.envoye ? `${name} recevra un e-mail avec un lien valable 7 jours.` : `${resultat.erreur || 'Envoi impossible'}. Vérifiez l'adresse puis renvoyez l'invitation depuis la liste.`}</p>
                </div>
            ) : (
                <div className="space-y-5">
                    {salaries.length > 0 && (
                        <Field label="Personne" help="Un salarié de votre établissement peut recevoir un compte d'équipe : son nom et son adresse sont repris de sa fiche.">{(f) => (
                            <Select {...f} value={employeId} onChange={(e) => choisirSalarie(e.target.value)}>
                                <option value="">Nouvelle personne</option>
                                {salaries.filter((s) => !s.deja_lie).map((s) => <option key={s.id} value={s.id}>Salarié existant : {s.nom}</option>)}
                            </Select>
                        )}</Field>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Nom complet" required error={champErreur.nom}>{(f) => <Input {...f} value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoFocus />}</Field>
                        <Field label="Adresse e-mail" required error={champErreur.email}>{(f) => <Input {...f} type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={120} />}</Field>
                    </div>
                    <fieldset>
                        <legend className="field-label">Rôle de départ</legend>
                        <div className="space-y-1.5 mt-1">
                            {ROLES.map((r) => <Radio key={r.value} name="role" checked={role === r.value} onChange={() => setRole(r.value)} label={r.label} sub={r.desc} />)}
                        </div>
                    </fieldset>
                    <fieldset>
                        <legend className="field-label">Périmètre</legend>
                        <div className="space-y-1.5 mt-1">
                            {services.map((s) => (
                                <Checkbox key={s.id} checked={svc.includes(s.id)} onChange={(e) => setSvc((l) => (e.target.checked ? [...l, s.id] : l.filter((x) => x !== s.id)))} label={s.name} />
                            ))}
                        </div>
                        {champErreur.svc && <span className="field-error" role="alert">{champErreur.svc}</span>}
                    </fieldset>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field label="Modèle de rôle" help="Ajustable ensuite, personne par personne.">{(f) => (
                            <Select {...f} value={modele} onChange={(e) => setModele(e.target.value)}>
                                <option value="">Aucun : régler ensuite</option><option value="hotel">Hôtel</option><option value="restaurant">Restaurant</option><option value="prestation">Prestation</option>
                            </Select>
                        )}</Field>
                        {role === 'prestataire' && <Field label="Accès valable jusqu'au" help="Après cette date, le compte ne peut plus se connecter.">{(f) => <Input {...f} type="date" value={expire} onChange={(e) => setExpire(e.target.value)} />}</Field>}
                    </div>
                    {phrase && <p className="rounded-xl bg-surface-soft border border-border px-4 py-3 text-[13px] text-content"><b>Aperçu :</b> {phrase}</p>}
                    {erreur && <p role="alert" className="text-[13px] text-state-danger">{erreur}</p>}
                </div>
            )}
        </Modal>
    );
};
